import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PricingBilling.css';
import BillingAddressModal from '../components/BillingAddressModal';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import AutopayConsent from '../components/AutopayConsent';
import { runOneTimePlanCheckout } from '../utils/subscriptionCheckout';
import { track } from '../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEFAULT_BILLING_CURRENCY = 'INR';

interface CatalogFeature {
  featureKey: string;
  label: string;
  unit: string;
  group: string;
  bundleEligible: boolean;
}

interface CatalogPlan {
  id: string;
  name: string;
  priceMonthly: number;
  currency?: 'INR' | 'USD';
  price?: number;
  status: 'active' | 'archived';
  sortOrder: number;
}

interface CatalogPaygItem extends CatalogFeature {
  pricePerUnit: number;
  currency?: 'INR' | 'USD';
  price?: number;
  maxPerMonth: number | null;
}

interface PricingCatalogResponse {
  boundaryTimezone: 'UTC';
  features: CatalogFeature[];
  payg: CatalogPaygItem[];
  plans: CatalogPlan[];
}

interface EffectiveEntitlement {
  max: number | null;
  source: 'user_override' | 'plan' | 'default' | 'fallback';
}

interface AssignedPlan {
  id: string;
  name: string;
  priceMonthly: number;
  status: 'active' | 'archived';
  sortOrder: number;
}

interface BillingStateResponse {
  mode: 'payg' | 'subscription';
  productEnabled: boolean;
  assignedPlan: AssignedPlan | null;
  effective: Record<string, EffectiveEntitlement>;
  usage: {
    currentPeriod: Record<string, number>;
  };
  paygEstimate: {
    currency: string;
    period: string;
    boundaryTimezone: 'UTC';
    lineItems: Array<{ featureKey: string; units: number; rate: number; amount: number }>;
    total: number;
  };
}

interface BillingPaymentMethodDto {
  id: string;
  methodType: string | null;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  issuerCountry: string | null;
  isInternational: boolean;
  isDefault: boolean;
  createdAt: string;
}

interface BillingListPaymentMethodsResponse {
  paymentMethods: BillingPaymentMethodDto[];
}

interface BillingInvoiceDto {
  id: string;
  invoiceType: 'subscription' | 'payg' | 'mixed';
  status: 'draft' | 'open' | 'paid' | 'void' | 'cancelled';
  amountDue: number;
  amountPaid: number;
  currency: string;
  paidAt: string | null;
  razorpayPaymentId: string | null;
  razorpayOrderId: string | null;
  invoicePdfUrl: string | null;
  createdAt: string;
}

interface BillingRefundDto {
  id: string;
  status: 'requested' | 'approved' | 'rejected' | 'processing' | 'processed' | 'failed' | 'cancelled';
  amount: number;
  currency: string;
  razorpayRefundId: string | null;
  razorpayPaymentId: string;
  reasonCode: string;
  createdAt: string;
}

interface BillingListInvoicesResponse {
  invoices: BillingInvoiceDto[];
  total: number;
}

interface BillingListRefundsResponse {
  refunds: BillingRefundDto[];
  total: number;
}

type BillingTransactionEntry = {
  id: string;
  kind: 'invoice' | 'refund';
  typeLabel: string;
  status: string;
  amount: number;
  currency: string;
  createdAt: string;
  reference: string;
  invoicePdfUrl?: string | null;
};

interface BillingPaymentMethodSetupSessionResponse {
  keyId: string;
  customerId: string;
  currency: string;
  checkout: {
    name: string;
    description: string;
    recurring: 1;
  };
  prefill: {
    email: string;
    name: string | null;
  };
  notes: {
    userId: string;
    purpose: 'payment_method_setup';
  };
}

interface BillingUserSubscriptionDto {
  id: string;
  userId: string;
  planId: string | null;
  razorpaySubscriptionId: string | null;
  currency: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelledAt: string | null;
  trialEnd: string | null;
  totalCount: number | null;
  createdAt: string;
  updatedAt: string;
}

interface BillingSubscriptionResponse {
  subscription: BillingUserSubscriptionDto | null;
}

interface BillingSubscribeResultResponse {
  subscription: BillingUserSubscriptionDto;
  checkout: {
    keyId: string;
    subscriptionId: string;
    shortUrl: string | null;
  };
}

interface AttachPaymentMethodResponse {
  paymentMethod: BillingPaymentMethodDto;
}

interface RazorpayCheckoutHandlerResponse {
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
  razorpay_subscription_id?: string;
}

interface RazorpayCheckoutFailure {
  error?: {
    code?: string;
    description?: string;
  };
}

interface RazorpayCheckoutOptions {
  key: string;
  customer_id?: string;
  recurring?: number;
  currency?: string;
  subscription_id?: string;
  name: string;
  description: string;
  method?: {
    card?: boolean;
    netbanking?: boolean;
    wallet?: boolean;
    upi?: boolean;
    emi?: boolean;
    paylater?: boolean;
  };
  prefill?: {
    email?: string;
    name?: string;
  };
  notes?: Record<string, string>;
  modal?: {
    ondismiss?: () => void;
  };
  handler?: (response: RazorpayCheckoutHandlerResponse) => void;
  theme?: {
    color: string;
  };
}

interface RazorpayCheckoutInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: RazorpayCheckoutFailure) => void) => void;
}

interface RazorpayCheckoutConstructor {
  new (options: RazorpayCheckoutOptions): RazorpayCheckoutInstance;
}

declare global {
  interface Window {
    Razorpay?: RazorpayCheckoutConstructor;
  }
}

let razorpayCheckoutScriptPromise: Promise<void> | null = null;

function loadRazorpayCheckoutScript(): Promise<void> {
  if (window.Razorpay) {
    return Promise.resolve();
  }

  if (razorpayCheckoutScriptPromise) {
    return razorpayCheckoutScriptPromise;
  }

  razorpayCheckoutScriptPromise = new Promise<void>((resolve, reject) => {
    const scriptSrc = 'https://checkout.razorpay.com/v1/checkout.js';
    const existing = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay Checkout SDK'));

    document.body.appendChild(script);
  });

  return razorpayCheckoutScriptPromise;
}

function waitFor(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function formatMoney(value: number, currency = DEFAULT_BILLING_CURRENCY): string {
  const rawCurrency = typeof currency === 'string' && currency.trim()
    ? currency.trim().toUpperCase()
    : DEFAULT_BILLING_CURRENCY;

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: rawCurrency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: DEFAULT_BILLING_CURRENCY,
      maximumFractionDigits: 2,
    }).format(value);
  }
}

function titleCase(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function getTransactionStatusClass(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized === 'paid' || normalized === 'processed' || normalized === 'approved') {
    return 'is-success';
  }
  if (normalized === 'failed' || normalized === 'rejected' || normalized === 'cancelled' || normalized === 'void') {
    return 'is-danger';
  }
  return 'is-warning';
}

const BillingPage: React.FC = () => {
  const [catalog, setCatalog] = useState<PricingCatalogResponse | null>(null);
  const [state, setState] = useState<BillingStateResponse | null>(null);
  const [subscription, setSubscription] = useState<BillingUserSubscriptionDto | null>(null);
  const [autopay, setAutopay] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<BillingPaymentMethodDto[]>([]);
  const [invoices, setInvoices] = useState<BillingInvoiceDto[]>([]);
  const [refunds, setRefunds] = useState<BillingRefundDto[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const [paymentNoticeTone, setPaymentNoticeTone] = useState<'success' | 'info' | 'warning'>('info');
  const [modeNotice, setModeNotice] = useState<string | null>(null);
  const [transactionsError, setTransactionsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: 'overview' | 'payments' = searchParams.get('tab') === 'payments' ? 'payments' : 'overview';
  const setActiveTab = (next: 'overview' | 'payments') => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    if (next === 'overview') params.delete('tab'); else params.set('tab', next);
    return params;
  }, { replace: true });
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const razorpayWebhookUrl = useMemo(() => {
    try {
      const parsed = new URL(API_BASE);
      return `${parsed.origin}/webhooks/razorpay`;
    } catch {
      return '/webhooks/razorpay';
    }
  }, []);

  const featureByKey = useMemo(() => {
    const map = new Map<string, CatalogFeature>();
    (catalog?.features || []).forEach((feature) => map.set(feature.featureKey, feature));
    return map;
  }, [catalog]);

  const refreshPaymentMethods = async (): Promise<boolean> => {
    setMethodsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/me/billing/payment-methods`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Failed to load payment methods' })) as { error?: string };
        throw new Error(data.error || 'Failed to load payment methods');
      }

      const data = await response.json() as BillingListPaymentMethodsResponse;
      setPaymentMethods(data.paymentMethods || []);
      setPaymentError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load payment methods right now.';
      setPaymentError(message);
      return false;
    } finally {
      setMethodsLoading(false);
    }
  };

  const refreshTransactions = async (): Promise<boolean> => {
    setTransactionsLoading(true);
    try {
      const [invoicesResponse, refundsResponse] = await Promise.all([
        fetch(`${API_BASE}/api/me/billing/invoices?limit=30&offset=0`, {
          credentials: 'include',
        }),
        fetch(`${API_BASE}/api/me/billing/refunds?limit=30&offset=0`, {
          credentials: 'include',
        }),
      ]);

      if (!invoicesResponse.ok || !refundsResponse.ok) {
        const invoicesErr = await invoicesResponse.json().catch(() => ({ error: 'Failed to load invoices' })) as { error?: string };
        const refundsErr = await refundsResponse.json().catch(() => ({ error: 'Failed to load refunds' })) as { error?: string };
        throw new Error(invoicesErr.error || refundsErr.error || 'Failed to load transaction history');
      }

      const [invoicesData, refundsData] = await Promise.all([
        invoicesResponse.json() as Promise<BillingListInvoicesResponse>,
        refundsResponse.json() as Promise<BillingListRefundsResponse>,
      ]);

      setInvoices(invoicesData.invoices || []);
      setRefunds(refundsData.refunds || []);
      setTransactionsError(null);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load transaction history right now.';
      setTransactionsError(message);
      return false;
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [catalogResponse, stateResponse, subscriptionResponse] = await Promise.all([
          fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/me/billing/subscription`, { credentials: 'include' }),
        ]);

        if (!catalogResponse.ok || !stateResponse.ok || !subscriptionResponse.ok) {
          throw new Error('Failed to load billing data');
        }

        const [catalogData, stateData, subscriptionData] = await Promise.all([
          catalogResponse.json() as Promise<PricingCatalogResponse>,
          stateResponse.json() as Promise<BillingStateResponse>,
          subscriptionResponse.json() as Promise<BillingSubscriptionResponse>,
        ]);

        if (!cancelled) {
          setCatalog(catalogData);
          setState(stateData);
          setSubscription(subscriptionData.subscription || null);
          setSelectedPlanId(subscriptionData.subscription?.planId || stateData.assignedPlan?.id || catalogData.plans[0]?.id || '');
        }

        if (!cancelled) {
          await refreshPaymentMethods();
        }

        if (!cancelled) {
          await refreshTransactions();
        }
      } catch {
        if (!cancelled) {
          setError('Unable to load billing information right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const runSubscriptionAction = async (path: 'pause' | 'resume' | 'cancel', successMessage: string) => {
    setSaving(true);
    setError(null);
    setModeNotice(null);
    try {
      const response = await fetch(`${API_BASE}/api/me/billing/${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Request failed' })) as { error?: string };
        throw new Error(data.error || 'Request failed');
      }
      track(`subscription_${path}`);
      setSubscription(await response.json() as BillingUserSubscriptionDto);
      const pricingResponse = await fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' });
      if (pricingResponse.ok) setState(await pricingResponse.json() as BillingStateResponse);
      setModeNotice(successMessage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePause = () => {
    const until = subscriptionPeriodEnd ? ` You can keep using your plan until ${subscriptionPeriodEnd}.` : '';
    if (!window.confirm(`Pause your subscription? Billing stops until you resume.${until}`)) return;
    void runSubscriptionAction('pause', 'Subscription paused.');
  };
  const handleResume = () => void runSubscriptionAction('resume', 'Subscription resumed.');
  const handleCancelSubscription = () => {
    if (!window.confirm('Cancel your subscription? You keep your plan until the current period ends.')) return;
    void runSubscriptionAction('cancel', 'Subscription will be cancelled at the end of the current period.');
  };

  const handleSwitchToPlan = () => {
    if (!selectedPlanId) {
      setError('Select a plan before switching to subscription mode.');
      return;
    }

    const startSubscriptionCheckout = async () => {
      setSaving(true);
      setError(null);
      setModeNotice(null);

      try {
        // Autopay off on a paid plan: one month through a Razorpay order, no subscription or mandate.
        const chosen = catalog?.plans.find((plan) => plan.id === selectedPlanId);
        if (!autopay && chosen && chosen.priceMonthly > 0) {
          await runOneTimePlanCheckout({ planId: selectedPlanId, planName: chosen.name, onStatus: setModeNotice });
          const [pricingResponse, subscriptionResponse] = await Promise.all([
            fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' }),
            fetch(`${API_BASE}/api/me/billing/subscription`, { credentials: 'include' }),
          ]);
          if (pricingResponse.ok) setState(await pricingResponse.json() as BillingStateResponse);
          if (subscriptionResponse.ok) {
            setSubscription(((await subscriptionResponse.json()) as BillingSubscriptionResponse).subscription || null);
          }
          setModeNotice(`${chosen.name} is active for one month.`);
          return;
        }

        const normalizedStatus = subscription?.status?.toLowerCase();
        const hasExistingSubscription = Boolean(
          subscription && normalizedStatus !== 'cancelled' && normalizedStatus !== 'completed'
        );

        const endpoint = hasExistingSubscription
          ? `${API_BASE}/api/me/billing/change-plan`
          : `${API_BASE}/api/me/billing/subscribe`;

        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planId: selectedPlanId }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({ error: 'Unable to start subscription checkout' }));
          throw new Error(data.error || 'Unable to start subscription checkout');
        }

        const data = await response.json() as BillingSubscribeResultResponse;
        setSubscription(data.subscription || null);

        const selectedPlanName = catalog?.plans.find((plan) => plan.id === selectedPlanId)?.name || 'selected plan';
        const isFreePlan = !data.checkout.keyId && !data.checkout.subscriptionId;
        setModeNotice(
          isFreePlan
            ? `${selectedPlanName} activated.`
            : `Subscription checkout started for ${selectedPlanName}. Complete Razorpay authorization to activate it.`
        );

        let modalCheckoutLaunched = false;
        let modalCheckoutCompleted = false;

        try {
          await loadRazorpayCheckoutScript();

          if (!window.Razorpay) {
            throw new Error('Razorpay Checkout SDK failed to initialize');
          }

          if (data.checkout.keyId && data.checkout.subscriptionId) {
            modalCheckoutLaunched = true;

            await new Promise<void>((resolve, reject) => {
              const checkout = new window.Razorpay!({
                key: data.checkout.keyId,
                subscription_id: data.checkout.subscriptionId,
                name: 'Viral AI',
                description: `${selectedPlanName} subscription`,
                notes: {
                  planId: selectedPlanId,
                  source: 'billing_page_subscription',
                },
                theme: {
                  color: '#10B981',
                },
                modal: {
                  ondismiss: () => reject(new Error('Checkout closed before authorization.')),
                },
                handler: (checkoutResponse) => {
                  if (!checkoutResponse.razorpay_payment_id) {
                    reject(new Error('Subscription authorization did not complete.'));
                    return;
                  }

                  modalCheckoutCompleted = true;
                  resolve();
                },
              });

              checkout.on('payment.failed', (failureResponse) => {
                const code = failureResponse.error?.code ? ` (${failureResponse.error.code})` : '';
                reject(new Error((failureResponse.error?.description || 'Subscription payment failed') + code));
              });

              checkout.open();
            });
          }
        } catch (checkoutErr) {
          if (!modalCheckoutLaunched && data.checkout.shortUrl) {
            const checkoutWindow = window.open(data.checkout.shortUrl, '_blank', 'noopener,noreferrer');
            if (!checkoutWindow) {
              window.location.href = data.checkout.shortUrl;
            }
            setModeNotice(`Subscription checkout opened for ${selectedPlanName}. Complete authorization and return to refresh status.`);
          } else {
            throw checkoutErr;
          }
        }

        if (modalCheckoutCompleted) {
          setModeNotice('Authorization received. Finalizing subscription status...');
        }

        // Webhook processing can lag a few seconds after checkout success.
        for (let attempt = 0; attempt < 4; attempt += 1) {
          const [latestPricingResponse, latestSubscriptionResponse] = await Promise.all([
            fetch(`${API_BASE}/api/me/pricing`, { credentials: 'include' }),
            fetch(`${API_BASE}/api/me/billing/subscription`, { credentials: 'include' }),
          ]);

          if (latestPricingResponse.ok) {
            const latestPricing = await latestPricingResponse.json() as BillingStateResponse;
            setState(latestPricing);
          }

          if (latestSubscriptionResponse.ok) {
            const latestSubscription = await latestSubscriptionResponse.json() as BillingSubscriptionResponse;
            const resolvedSubscription = latestSubscription.subscription || data.subscription || null;
            setSubscription(resolvedSubscription);

            if (resolvedSubscription?.status?.toLowerCase() === 'active') {
              setModeNotice(`Subscription activated for ${selectedPlanName}.`);
              break;
            }
          }

          if (attempt < 3) {
            await waitFor(1500);
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to start subscription checkout';
        setError(message);
      } finally {
        setSaving(false);
      }
    };

    // A paid plan needs an invoice address first; the modal re-runs this handler once it is saved.
    const chosenPlan = catalog?.plans.find((plan) => plan.id === selectedPlanId);
    if (chosenPlan && chosenPlan.priceMonthly > 0) {
      void (async () => {
        try {
          const res = await fetch(`${API_BASE}/api/me/billing-address`, { credentials: 'include' });
          if (!res.ok) throw new Error('address check failed');
          const data = await res.json() as { address: unknown | null };
          if (!data.address) {
            setAddressModalOpen(true);
            return;
          }
        } catch {
          setError('Could not check your billing address. Please try again.');
          return;
        }
        void startSubscriptionCheckout();
      })();
      return;
    }

    void startSubscriptionCheckout();
  };

  const handleAddPaymentMethod = async () => {
    setPaymentActionLoading(true);
    setPaymentError(null);
    setPaymentNotice(null);
    setPaymentNoticeTone('info');

    try {
      await loadRazorpayCheckoutScript();

      const setupResponse = await fetch(`${API_BASE}/api/me/billing/payment-methods/setup`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!setupResponse.ok) {
        const data = await setupResponse.json().catch(() => ({ error: 'Unable to start payment setup' })) as { error?: string };
        throw new Error(data.error || 'Unable to start payment setup');
      }

      const setup = await setupResponse.json() as BillingPaymentMethodSetupSessionResponse;

      if (setup.keyId.startsWith('rzp_live_')) {
        setPaymentNoticeTone('warning');
        setPaymentNotice('Live mode is enabled. Razorpay test cards will not work; use a real payment method.');
      } else if (setup.keyId.startsWith('rzp_test_')) {
        setPaymentNoticeTone('info');
        setPaymentNotice('Sandbox mode enabled. Use test card 4111 1111 1111 1111, any future expiry, CVV 123, OTP 123456.');
      }

      if (!window.Razorpay) {
        throw new Error('Razorpay Checkout SDK failed to initialize');
      }

      const checkoutResponse = await new Promise<RazorpayCheckoutHandlerResponse>((resolve, reject) => {
        const checkout = new window.Razorpay!({
          key: setup.keyId,
          customer_id: setup.customerId,
          recurring: Number(setup.checkout.recurring),
          currency: setup.currency,
          name: setup.checkout.name,
          description: setup.checkout.description,
          method: {
            card: true,
            netbanking: false,
            wallet: false,
            upi: false,
            emi: false,
            paylater: false,
          },
          prefill: {
            email: setup.prefill.email,
            name: setup.prefill.name || undefined,
          },
          notes: {
            userId: setup.notes.userId,
            purpose: setup.notes.purpose,
          },
          theme: {
            color: '#10B981',
          },
          modal: {
            ondismiss: () => reject(new Error('Checkout closed before authorization. Complete card details and OTP to save sandbox payment method.')),
          },
          handler: (response) => resolve(response),
        });

        checkout.on('payment.failed', (response) => {
          const code = response.error?.code ? ` (${response.error.code})` : '';
          reject(new Error((response.error?.description || 'Payment authorization failed') + code));
        });

        checkout.open();
      });

      const paymentId = checkoutResponse.razorpay_payment_id;
      if (!paymentId) {
        throw new Error('Unable to confirm payment method: missing payment id from Razorpay Checkout.');
      }

      const attachResponse = await fetch(`${API_BASE}/api/me/billing/payment-methods/attach`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentId }),
      });

      if (!attachResponse.ok) {
        const data = await attachResponse.json().catch(() => ({ error: 'Unable to save payment method after checkout' })) as { error?: string };
        throw new Error(data.error || 'Unable to save payment method after checkout');
      }

      await attachResponse.json().catch(() => null) as AttachPaymentMethodResponse | null;

      const refreshed = await refreshPaymentMethods();
      if (refreshed) {
        setPaymentNoticeTone('success');
        setPaymentNotice('Payment method setup completed successfully.');
      } else {
        setPaymentNoticeTone('info');
        setPaymentNotice('Payment setup completed. Refresh payment methods to confirm latest status.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to add payment method right now.';
      if (message.toLowerCase().includes('closed before authorization')) {
        setPaymentNoticeTone('warning');
        setPaymentNotice('Checkout was closed before authorization. Finish card details and OTP to complete sandbox setup.');
      } else {
        setPaymentError(message);
      }
    } finally {
      setPaymentActionLoading(false);
    }
  };

  const effectiveRows = useMemo(() => {
    if (!state) return [] as Array<{ featureKey: string; label: string; max: number | null; source: string }>;

    return Object.entries(state.effective).map(([featureKey, value]) => ({
      featureKey,
      label: featureByKey.get(featureKey)?.label || featureKey,
      max: value.max,
      source: value.source,
    }));
  }, [state, featureByKey]);

  const subscriptionStatus = subscription?.status?.toLowerCase() || null;
  const isSubscriptionMode = subscriptionStatus === 'active';
  const planNameFromCatalog = subscription?.planId
    ? (catalog?.plans.find((plan) => plan.id === subscription.planId)?.name || null)
    : null;
  const planNameFromPricingState = subscription?.planId && state?.assignedPlan?.id === subscription.planId
    ? state.assignedPlan.name
    : null;
  const currentPlanId = state?.assignedPlan?.id || (isSubscriptionMode ? subscription?.planId : null) || null;
  const normalizedSubscriptionStatus = subscription?.status?.toLowerCase() || '';
  const isAutoRenewing = (subscription?.totalCount ?? 0) > 1;
  const isPaused = normalizedSubscriptionStatus === 'paused';
  const canManageSubscription = isAutoRenewing && (normalizedSubscriptionStatus === 'active' || isPaused);
  const subscriptionPeriodEnd = subscription?.currentPeriodEnd ? formatDateTime(subscription.currentPeriodEnd) : null;
  const selectedPlanForConsent = catalog?.plans.find((plan) => plan.id === selectedPlanId);
  const resolvedSubscriptionPlanName = planNameFromCatalog || planNameFromPricingState || state?.assignedPlan?.name || 'Subscription';
  const totalUsageUnits = state
    ? Object.values(state.usage.currentPeriod).reduce((sum, units) => sum + units, 0)
    : 0;

  const transactions = useMemo(() => {
    const invoiceEntries: BillingTransactionEntry[] = invoices.map((invoice) => ({
      id: `invoice-${invoice.id}`,
      kind: 'invoice',
      typeLabel: titleCase(invoice.invoiceType),
      status: titleCase(invoice.status),
      amount: invoice.amountPaid > 0 ? invoice.amountPaid : invoice.amountDue,
      currency: invoice.currency,
      createdAt: invoice.paidAt || invoice.createdAt,
      reference: invoice.razorpayPaymentId || invoice.razorpayOrderId || invoice.id,
      invoicePdfUrl: invoice.invoicePdfUrl,
    }));

    const refundEntries: BillingTransactionEntry[] = refunds.map((refund) => ({
      id: `refund-${refund.id}`,
      kind: 'refund',
      typeLabel: 'Refund',
      status: titleCase(refund.status),
      amount: -Math.abs(refund.amount),
      currency: refund.currency,
      createdAt: refund.createdAt,
      reference: refund.razorpayRefundId || refund.razorpayPaymentId || refund.id,
      invoicePdfUrl: null,
    }));

    return [...invoiceEntries, ...refundEntries].sort((a, b) => {
      const aTs = new Date(a.createdAt).getTime();
      const bTs = new Date(b.createdAt).getTime();
      return bTs - aTs;
    });
  }, [invoices, refunds]);

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="page-header">
          <div className="page-header-left">
            <h2>Billing</h2>
          </div>
          {!loading && !error && state && (
            <div className="dashboard-plan-badge">
              <span className="dashboard-plan-name">{state.assignedPlan?.name || 'Free'} Plan</span>
              {state.assignedPlan && <span className="dashboard-plan-status">{state.assignedPlan.status}</span>}
            </div>
          )}
        </div>
        <p className="page-subtitle">Review your current pricing mode, limits, and estimated PAYG charges.</p>

        <div className="pricing-shell billing-page-shell billing-page-shell--embedded">
          {loading && <div className="pricing-loading">Loading billing details...</div>}
          {error && <div className="pricing-error">{error}</div>}

          {!loading && !error && state && (
            <>
              <div className="billing-tabs" role="tablist" aria-label="Billing sections">
                <button
                  type="button"
                  role="tab"
                  id="billing-tab-overview"
                  aria-selected={activeTab === 'overview'}
                  aria-controls="billing-panel-overview"
                  className={`billing-tab ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  type="button"
                  role="tab"
                  id="billing-tab-payments"
                  aria-selected={activeTab === 'payments'}
                  aria-controls="billing-panel-payments"
                  className={`billing-tab ${activeTab === 'payments' ? 'active' : ''}`}
                  onClick={() => setActiveTab('payments')}
                >
                  Payment History
                </button>
              </div>
              <div className="billing-tab-panel" role="tabpanel" id="billing-panel-overview" aria-labelledby="billing-tab-overview" hidden={activeTab !== 'overview'}>
                <div className="pricing-card billing-surface-card">
                  <h3>Current Plan</h3>
                  <p className="pricing-price">{state.assignedPlan?.name || (isSubscriptionMode ? resolvedSubscriptionPlanName : 'Free')} Plan</p>
                  {!state.productEnabled && (
                    <p className="pricing-error">Your product access is disabled. Plan switching is currently unavailable.</p>
                  )}
                  {modeNotice && <div className="billing-notice billing-notice-success">{modeNotice}</div>}
                  {subscription && (
                    <p className="pricing-note billing-card-note">
                      Subscription status: {titleCase(subscription.status)}
                    </p>
                  )}
                  {subscription && normalizedSubscriptionStatus === 'active' && !isAutoRenewing && subscriptionPeriodEnd && (
                    <p className="pricing-note billing-card-note">One-month plan, no automatic renewal. Ends on {subscriptionPeriodEnd}.</p>
                  )}
                  {isPaused && subscriptionPeriodEnd && (
                    <p className="pricing-note billing-card-note">Billing is paused. Your plan stays available until {subscriptionPeriodEnd}, then moves to Free until you resume.</p>
                  )}
                  {canManageSubscription && subscription?.cancelAtPeriodEnd && subscriptionPeriodEnd && (
                    <p className="pricing-note billing-card-note">Cancels on {subscriptionPeriodEnd}. No further charges.</p>
                  )}
                  {canManageSubscription && !subscription?.cancelAtPeriodEnd && (
                    <div className="billing-actions">
                      {isPaused ? (
                        <button className="billing-btn" disabled={saving} onClick={handleResume}>Resume subscription</button>
                      ) : (
                        <button className="billing-btn secondary" disabled={saving} onClick={handlePause}>Pause subscription</button>
                      )}
                      <button className="billing-btn secondary" disabled={saving} onClick={handleCancelSubscription}>Cancel subscription</button>
                    </div>
                  )}
                  <div className="billing-actions">
                    <select
                      className="billing-select"
                      value={selectedPlanId}
                      disabled={saving || !state.productEnabled}
                      onChange={(e) => setSelectedPlanId(e.target.value)}
                    >
                      {catalog?.plans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name} ({formatMoney(plan.price ?? plan.priceMonthly, plan.currency ?? 'USD')}/mo)
                        </option>
                      ))}
                    </select>
                    <button className="billing-btn" disabled={saving || !state.productEnabled || !selectedPlanId || selectedPlanId === currentPlanId} title={selectedPlanId === currentPlanId ? 'You are already on this plan' : undefined} onClick={handleSwitchToPlan}>
                      Switch to Plan
                    </button>
                  </div>
                  {selectedPlanForConsent && selectedPlanForConsent.priceMonthly > 0 && selectedPlanId !== currentPlanId && (
                    <AutopayConsent
                      checked={autopay}
                      onChange={setAutopay}
                      amountLabel={formatMoney(selectedPlanForConsent.price ?? selectedPlanForConsent.priceMonthly, selectedPlanForConsent.currency ?? 'USD')}
                      disabled={saving}
                    />
                  )}

                  <div className="billing-mode-meta">
                    <span>Current period usage</span>
                    <strong>{totalUsageUnits} units</strong>
                  </div>
                </div>

                <h2 className="pricing-section-title billing-section-title">Pay As You Go Rates</h2>
                <div className="pricing-table-wrap">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Rate</th>
                        <th>Monthly Cap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(catalog?.payg || []).map((item) => (
                        <tr key={item.featureKey}>
                          <td>{item.label}</td>
                          <td>{formatMoney(item.price ?? item.pricePerUnit, item.currency ?? state.paygEstimate.currency)} / {item.unit}</td>
                          <td>{item.maxPerMonth === null ? 'No cap' : item.maxPerMonth}</td>
                        </tr>
                      ))}
                      {(catalog?.payg || []).length === 0 && (
                        <tr>
                          <td colSpan={3}>No PAYG rates available.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="pricing-feature-panels">
                  <div className="pricing-feature-card">
                    <h3>Razorpay Setup Steps</h3>
                    <ul className="pricing-inline-list">
                      <li>Use Checkout to authorize the card and save a payment method immediately.</li>
                      <li>Configure Razorpay webhook URL: {razorpayWebhookUrl}</li>
                      <li>Use the same webhook secret in server env as RAZORPAY_WEBHOOK_SECRET.</li>
                      <li>Enable events: payment.authorized, payment.captured, payment.failed, subscription.*, refund.*</li>
                    </ul>
                  </div>

                  <div className="pricing-feature-card">
                    <h3>Payment Confirmation</h3>
                    <p>
                      When a payment or plan update succeeds in the payment wall, the blocked action resumes automatically.
                    </p>
                    <ul className="pricing-inline-list">
                      <li>Confirmation includes feature, units, and amount or selected plan.</li>
                      <li>If resume fails, retry options are shown without losing context.</li>
                    </ul>
                  </div>

                  <div className="pricing-feature-card">
                    <h3>Benefits</h3>
                    <ul className="pricing-inline-list">
                      <li>PAYG: immediate continuation with exact per-action cost.</li>
                      <li>Subscription: included monthly limits and better value for repeated usage.</li>
                    </ul>
                  </div>
                </div>

                <h2 className="pricing-section-title billing-section-title">Effective Limits</h2>
                <div className="pricing-table-wrap">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Effective Max</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {effectiveRows.map((row) => (
                        <tr key={row.featureKey}>
                          <td>{row.label}</td>
                          <td>{row.max === null ? 'Unlimited' : row.max}</td>
                          <td>{titleCase(row.source)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="pricing-section-title billing-section-title">Current Period Usage</h2>
                <div className="pricing-table-wrap">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Units Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(state.usage.currentPeriod).map(([featureKey, units]) => (
                        <tr key={featureKey}>
                          <td>{featureByKey.get(featureKey)?.label || featureKey}</td>
                          <td>{units}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className="pricing-section-title billing-section-title">PAYG Estimate ({state.paygEstimate.period}, UTC)</h2>
                <div className="pricing-table-wrap">
                  <table className="pricing-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Billable Units</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.paygEstimate.lineItems.length === 0 && (
                        <tr>
                          <td colSpan={4}>No current PAYG overage for this period.</td>
                        </tr>
                      )}
                      {state.paygEstimate.lineItems.map((line) => (
                        <tr key={line.featureKey}>
                          <td>{featureByKey.get(line.featureKey)?.label || line.featureKey}</td>
                          <td>{line.units}</td>
                          <td>{formatMoney(line.rate, state.paygEstimate.currency)}</td>
                          <td>{formatMoney(line.amount, state.paygEstimate.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="pricing-note">Estimated total: {formatMoney(state.paygEstimate.total, state.paygEstimate.currency)}</p>
              </div>
              <div className="billing-tab-panel" role="tabpanel" id="billing-panel-payments" aria-labelledby="billing-tab-payments" hidden={activeTab !== 'payments'}>
                <div className="pricing-card billing-surface-card">
                  <h3>Payment Methods</h3>
                  <p className="pricing-note billing-card-note">
                    Add a payment method once and use it for PAYG authorization and subscription billing.
                  </p>

                  <div className="billing-actions">
                    <button className="billing-btn" type="button" disabled={paymentActionLoading} onClick={handleAddPaymentMethod}>
                      {paymentActionLoading ? 'Opening checkout...' : 'Checkout'}
                    </button>
                    <button
                      className="billing-btn secondary"
                      type="button"
                      disabled={methodsLoading || paymentActionLoading}
                      onClick={() => { void refreshPaymentMethods(); }}
                    >
                      Refresh methods
                    </button>
                  </div>

                  {methodsLoading && <div className="pricing-loading">Loading payment methods...</div>}
                  {paymentNotice && <div className={`billing-notice billing-notice-${paymentNoticeTone}`}>{paymentNotice}</div>}
                  {paymentError && <div className="pricing-error">{paymentError}</div>}

                  {!methodsLoading && paymentMethods.length === 0 && (
                    <p className="pricing-note billing-empty-state">No payment methods saved yet.</p>
                  )}

                  {!methodsLoading && paymentMethods.length > 0 && (
                    <div className="billing-method-list">
                      {paymentMethods.map((method) => (
                        <div key={method.id} className="billing-method-row">
                          <div>
                            <p className="billing-method-title">
                              {(method.brand || method.methodType || 'Card').toUpperCase()} •••• {method.last4 || '----'}
                            </p>
                            <p className="billing-method-meta">
                              {method.expMonth && method.expYear
                                ? `Exp ${String(method.expMonth).padStart(2, '0')}/${method.expYear}`
                                : 'Expiry unavailable'}
                            </p>
                          </div>
                          {method.isDefault && <span className="billing-method-tag">Default</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <h2 className="pricing-section-title billing-section-title">Transaction History</h2>
                <div className="pricing-card billing-surface-card">
                  <div className="billing-actions">
                    <button
                      className="billing-btn secondary"
                      type="button"
                      disabled={transactionsLoading}
                      onClick={() => { void refreshTransactions(); }}
                    >
                      {transactionsLoading ? 'Refreshing history...' : 'Refresh history'}
                    </button>
                  </div>

                  {transactionsLoading && <div className="pricing-loading">Loading transaction history...</div>}
                  {transactionsError && <div className="pricing-error">{transactionsError}</div>}

                  {!transactionsLoading && !transactionsError && transactions.length === 0 && (
                    <p className="pricing-note billing-empty-state">No transactions available yet.</p>
                  )}

                  {!transactionsLoading && !transactionsError && transactions.length > 0 && (
                    <div className="pricing-table-wrap">
                      <table className="pricing-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Reference</th>
                            <th>Receipt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((item) => (
                            <tr key={item.id}>
                              <td>{formatDateTime(item.createdAt)}</td>
                              <td>{item.typeLabel}</td>
                              <td>
                                <span className={`billing-transaction-status ${getTransactionStatusClass(item.status)}`}>
                                  {item.status}
                                </span>
                              </td>
                              <td className={`billing-transaction-amount ${item.amount < 0 ? 'refund' : 'charge'}`}>
                                {item.amount < 0 ? '-' : ''}{formatMoney(Math.abs(item.amount), item.currency)}
                              </td>
                              <td className="billing-transaction-reference">{item.reference}</td>
                              <td>
                                {item.kind === 'invoice' && item.invoicePdfUrl ? (
                                  <a
                                    href={item.invoicePdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="billing-transaction-link"
                                  >
                                    PDF
                                  </a>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {addressModalOpen && (
            <BillingAddressModal
              onClose={() => setAddressModalOpen(false)}
              onSaved={() => {
                setAddressModalOpen(false);
                handleSwitchToPlan();
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default BillingPage;
